import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Camera, Trash, UploadSimple, Lightning, CalendarBlank } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhotosTab({ clientId }) {
  const [photos, setPhotos] = useState([]);
  const [phases, setPhases] = useState([]);
  const [photoBlobs, setPhotoBlobs] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [filterPhase, setFilterPhase] = useState("all");

  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadDate, setUploadDate] = useState(new Date().toISOString().split("T")[0]);
  const [uploadPhaseId, setUploadPhaseId] = useState("none");
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [photosRes, phasesRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/photos`),
        axios.get(`${API}/clients/${clientId}/phases`),
      ]);
      setPhotos(photosRes.data);
      setPhases(phasesRes.data);
    } catch {
      // Error handled silently
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    photos.forEach(async (photo) => {
      if (!photoBlobs[photo.id] && photo.storage_path) {
        try {
          const response = await axios.get(`${API}/files/${photo.storage_path}`, { responseType: "blob" });
          const blobUrl = URL.createObjectURL(response.data);
          setPhotoBlobs((prev) => ({ ...prev, [photo.id]: blobUrl }));
        } catch (err) { console.debug("[PhotosTab]", err); }
      }
    });
    return () => {
      Object.values(photoBlobs).forEach((url) => URL.revokeObjectURL(url));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadFiles(files);
    setUploadDate(new Date().toISOString().split("T")[0]);
    setUploadPhaseId("none");
    setShowUploadDialog(true);
    e.target.value = "";
  };

  const handleUploadConfirm = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", "progress");
        formData.append("date", uploadDate);
        formData.append("phase_id", uploadPhaseId === "none" ? "" : uploadPhaseId);
        await axios.post(`${API}/clients/${clientId}/photos`, formData);
      }
      toast.success(`${uploadFiles.length} foto caricate`);
      setShowUploadDialog(false);
      setUploadFiles([]);
      fetchData();
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/photos/${photoId}`);
      toast.success("Foto eliminata");
      if (photoBlobs[photoId]) {
        URL.revokeObjectURL(photoBlobs[photoId]);
        setPhotoBlobs((prev) => { const n = { ...prev }; delete n[photoId]; return n; });
      }
      fetchData();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const getPhaseTitle = (phaseId) => phases.find((p) => p.id === phaseId)?.title || "";

  // Filter and group by date
  const filtered = filterPhase === "all"
    ? photos
    : filterPhase === "no_phase"
      ? photos.filter((p) => !p.phase_id)
      : photos.filter((p) => p.phase_id === filterPhase);

  const grouped = {};
  filtered.forEach((p) => {
    const date = p.date || p.created_at?.split("T")[0] || "Senza data";
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(p);
  });
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div data-testid="photos-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h3 className="text-xl font-bold tracking-tight uppercase text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          GALLERIA FOTO
        </h3>
        <div className="flex items-center gap-3">
          <Select value={filterPhase} onValueChange={setFilterPhase}>
            <SelectTrigger className="w-44 bg-[#0A0A0A] border-[#262626] text-white text-sm" data-testid="filter-phase-photos">
              <SelectValue placeholder="Tutte le fasi" />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626]">
              <SelectItem value="all" className="text-white">Tutte le fasi</SelectItem>
              <SelectItem value="no_phase" className="text-white">Senza fase</SelectItem>
              {phases.map((p) => (
                <SelectItem key={p.id} value={p.id} className="text-white">{p.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label>
            <input type="file" accept="image/*" multiple onChange={handleFilesSelected} className="hidden" data-testid="photo-upload-input" />
            <Button asChild className="bg-[#007AFF] hover:bg-[#3395FF] text-white cursor-pointer">
              <span data-testid="upload-photo-button">
                <UploadSimple size={18} className="mr-2" /> Carica Foto
              </span>
            </Button>
          </label>
        </div>
      </div>

      {sortedDates.length > 0 ? (
        <div className="space-y-6">
          {sortedDates.map((date) => {
            const datePhotos = grouped[date];
            const phaseId = datePhotos[0]?.phase_id;
            const phaseTitle = phaseId ? getPhaseTitle(phaseId) : "";
            return (
              <div key={date}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <CalendarBlank size={14} className="text-[#007AFF]" />
                    <span className="text-sm font-bold text-white">{date}</span>
                  </div>
                  {phaseTitle && (
                    <span className="text-[10px] px-2 py-0.5 bg-[#D4FF00]/10 text-[#D4FF00] rounded-full font-medium flex items-center gap-1">
                      <Lightning size={9} weight="fill" />{phaseTitle}
                    </span>
                  )}
                  <span className="text-[10px] text-zinc-600">{datePhotos.length} foto</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {datePhotos.map((photo, i) => (
                    <motion.div
                      key={photo.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="group relative overflow-hidden rounded-lg bg-[#171717] border border-[#262626] aspect-square cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                      data-testid={`photo-card-${photo.id}`}
                    >
                      {photoBlobs[photo.id] ? (
                        <img
                          src={photoBlobs[photo.id]}
                          alt={photo.original_filename}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between pointer-events-auto">
                          <p className="text-xs text-white truncate">{photo.original_filename}</p>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                            className="text-white/70 hover:text-red-400 transition-colors"
                            data-testid={`delete-photo-${photo.id}`}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#171717] border border-[#262626] rounded-lg">
          <Camera size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">{filterPhase !== "all" ? "Nessuna foto per questa fase" : "Nessuna foto caricata"}</p>
          <p className="text-zinc-600 text-sm mt-1">Carica foto del prima e dopo per tracciare i progressi</p>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-md" data-testid="upload-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              CARICA FOTO
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]">
              <p className="text-sm text-white">{uploadFiles.length} {uploadFiles.length === 1 ? "foto" : "foto"} selezionate</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {uploadFiles.map((f, i) => (
                  <span key={`file-${i}-${f.name}`} className="text-[10px] text-zinc-500 bg-[#171717] px-2 py-0.5 rounded">{f.name}</span>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data</Label>
              <Input
                type="date"
                value={uploadDate}
                onChange={(e) => setUploadDate(e.target.value)}
                className="mt-1 bg-[#0A0A0A] border-[#262626] text-white"
                data-testid="upload-photo-date"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fase</Label>
              <Select value={uploadPhaseId} onValueChange={setUploadPhaseId}>
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="upload-photo-phase">
                  <SelectValue placeholder="Nessuna fase" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="none" className="text-white">Nessuna fase</SelectItem>
                  {phases.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-white">{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]" data-testid="upload-photo-cancel">Annulla</Button>
            <Button onClick={handleUploadConfirm} disabled={uploading} className="bg-[#007AFF] hover:bg-[#3395FF] text-white" data-testid="upload-photo-confirm">
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <UploadSimple size={16} className="mr-2" />
              )}
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)} data-testid="photo-lightbox">
          <div className="max-w-4xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            {photoBlobs[selectedPhoto.id] && (
              <img src={photoBlobs[selectedPhoto.id]} alt={selectedPhoto.original_filename} className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            )}
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-400">{selectedPhoto.date || selectedPhoto.created_at?.split("T")[0]}</span>
                {selectedPhoto.phase_id && getPhaseTitle(selectedPhoto.phase_id) && (
                  <span className="text-[10px] px-2 py-0.5 bg-[#D4FF00]/10 text-[#D4FF00] rounded-full font-medium flex items-center gap-1">
                    <Lightning size={9} weight="fill" />{getPhaseTitle(selectedPhoto.phase_id)}
                  </span>
                )}
              </div>
              <button onClick={() => setSelectedPhoto(null)} className="text-white/70 hover:text-white text-sm" data-testid="close-lightbox">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
